//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useEffect, useMemo, useState } from 'react';
import { ChatContext, type ChatContextValue } from '@/contexts/ChatContext';
import { type ChatMessage } from '@/components/Chat/ChatTypes';
import { useAuth } from '@/hooks/useAuth';
import { usePersistentState } from '@/hooks/usePersistentState';

interface ChatProviderProps {
  children: React.ReactNode;
}

// Owns the chat transcript.  Geometry and open state belong to the panel
// registry, which every panel client shares.
const ChatProvider = ({ children }: ChatProviderProps) => {
  const { userProfile } = useAuth();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] =
    usePersistentState<string | null>('agent-conversation', null, false,
      (stored) => stored === null || typeof stored === 'string');

  // A logout is a client-side navigation, so a transcript held here would
  // still be here for whoever signs in next on a shared workstation.
  useEffect(() => {
    if (userProfile) return;
    setChatMessages((prev) => prev.length === 0 ? prev : []);
  }, [userProfile]);

  const value = useMemo((): ChatContextValue => ({
    chatMessages, setChatMessages, conversationId, setConversationId
  }), [chatMessages, conversationId, setConversationId]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;
